import { nullable, z } from 'zod';

import type { ExcelError } from '@apple/utils/files/models/excel';

/** @see: Manzanita.Web.Areas.Admin.Models.Bulk.BulkUploadItemViewModel */
export interface BulkUploadItem {
	rowNumber: number;
	action: string;
	isValid: boolean;
	validationErrors: string[];
}

type BulkUploadInvalidRow<T extends BulkUploadItem> = {
	[Key in keyof T]: Key extends keyof BulkUploadItem ? T[Key] : T[Key] | null;
};

/** @see: Manzanita.Web.Areas.Admin.Models.Bulk.BulkUploadResponseModel */
export interface BulkUploadResponse<T extends BulkUploadItem> {
	uploadedRows: T[];
	invalidRows: BulkUploadInvalidRow<T>[];
	fileId: number;
	criticalErrors: string[];
	numberOfImportedRows: number;
	numberOfInsertedRows: number;
	numberOfUpdatedRows: number;
	numberOfDeletedRows: number;
	numberOfIgnoredRows: number;
	numberOfInvalidRows: number;
}

/** @see: Manzanita.Web.Areas.Admin.Models.Bulk.BulkUploadItemViewModel */
export const bulkUploadItemSchema = z.object({
	rowNumber: z.number().int(),
	action: z.string().min(1),
	isValid: z.boolean(),
	validationErrors: z.array(z.string()),
}) satisfies z.ZodType<BulkUploadItem>;

const bulkUploadItemKeys = new Set<string>(['rowNumber', 'action', 'isValid', 'validationErrors']);

type BulkUploadInvalidRowShape<TShape extends z.ZodRawShape> = {
	[Key in keyof TShape]: Key extends keyof BulkUploadItem
		? TShape[Key]
		: z.ZodNullable<TShape[Key]>;
};

function createBulkUploadInvalidRowSchema<TShape extends z.ZodRawShape>(
	bulkUploadItem: z.ZodObject<TShape>,
): z.ZodObject<BulkUploadInvalidRowShape<TShape>> {
	const invalidRowShape = Object.fromEntries(
		Object.entries(bulkUploadItem.shape).map(([key, schema]) => [
			key,
			bulkUploadItemKeys.has(key) ? schema : nullable(schema),
		]),
	) as BulkUploadInvalidRowShape<TShape>;

	return z.object(invalidRowShape);
}

export function createBulkUploadResponseSchema<TShape extends z.ZodRawShape>(
	bulkUploadItem: z.ZodObject<TShape> & z.ZodType<BulkUploadItem>,
) {
	const invalidRow = createBulkUploadInvalidRowSchema(bulkUploadItem);

	return z
		.object({
			uploadedRows: z.array(bulkUploadItem),
			invalidRows: z.array(invalidRow),
			fileId: z.number(),
			criticalErrors: z.array(z.string()),
			numberOfImportedRows: z.number(),
			numberOfInsertedRows: z.number(),
			numberOfUpdatedRows: z.number(),
			numberOfDeletedRows: z.number(),
			numberOfIgnoredRows: z.number(),
			numberOfInvalidRows: z.number(),
		})
		.readonly();
}

function isBulkUpload<
	TItem extends Record<string, unknown>,
	TKey extends keyof TItem & string,
	TError extends ExcelError<TItem, TKey>,
>(errors: TError[] | BulkUploadItem[]): errors is BulkUploadItem[] {
	return errors.length > 0 && errors[0] !== undefined && 'validationErrors' in errors[0];
}

export function normalizeErrors<
	TItem extends Record<string, unknown>,
	TKey extends keyof TItem & string,
	TError extends ExcelError<TItem, TKey>,
>(errors: TError[] | BulkUploadItem[], keyProperty: TKey) {
	return isBulkUpload(errors)
		? errors.map(item => ({
				[keyProperty]: item.rowNumber,
				errors: { validationErrors: item.validationErrors },
			}))
		: errors;
}
